import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { getUserPremiumStatus, getUserDetails, getMeter } from '@surfline/web-common';
import SurfCheckCounter from '../../components/SurfCheckCounter';

/**
 * React Hook for Metering Modals. This is a centralized location for all Meter related logic.
 * Depending on the meter state of a user this hook computes whether or not to show the Meter Modal.
 * The hook also determines which Modal to be rendered based on the treatment variant the user is in.
 *
 * @return Object meterState
 */
const useMetering = () => {
  const meter = useSelector(getMeter, shallowEqual);
  const userDetails = useSelector(getUserDetails, shallowEqual);
  const isAnonymous = !userDetails;
  const isPremium = useSelector(getUserPremiumStatus);

  const defaultMeterState = {
    showMeterRegwall: false,
    showSurfCheckCounter: false,
    treatmentVariant: null,
    meterRemaining: null,
    modalToRender: null,
    isMeteredPremium: false,
  };

  // If the user is Premium then don't show the Modals and go through any of this rate limit logic
  if (isPremium) {
    return defaultMeterState;
  }

  const { treatmentVariant, meterRemaining, meterLimit } = meter || {};
  const meterState = {
    ...defaultMeterState,
    treatmentVariant,
    meterRemaining,
    meterLimit,
  };
  // If there is no meter then don't show the Modals
  if (!treatmentVariant) {
    return defaultMeterState;
  }

  // Anonymous Sessions
  if (isAnonymous) {
    // If treatment is OFF then don't show the Modals
    if (treatmentVariant.match(/^(off|control)$/)) {
      return {
        ...meterState,
        showMeterRegwall: false,
        isMeteredPremium: false,
        modalToRender: null,
      };
    }
    // If the variant is surf_check or account_benefits then compute the surf check eligibility
    // Eligible for a surf check if meterRemaining > 0.
    const isSurfCheckEligible = meterRemaining > 0;
    return {
      ...meterState,
      showMeterRegwall: !isSurfCheckEligible,
      isMeteredPremium: isSurfCheckEligible,
      modalToRender: null,
    };
  }

  // Registered Users
  if (!isPremium) {
    // If treatment is OFF then don't show the Modals
    if (treatmentVariant.match(/^(off|control)$/)) {
      return {
        ...meterState,
        showMeterRegwall: false,
        isMeteredPremium: false,
        showSurfCheckCounter: false,
        modalToRender: null,
      };
    }
    // If the variant is surf_check or account_benefits then compute the surf check eligibility
    // Eligible for a surf check if meterRemaining > 0. Show the Surf Check Counter Modal.
    const isSurfCheckEligible = meterRemaining > 0;
    const modalToRender = isSurfCheckEligible ? <SurfCheckCounter /> : null;
    return {
      ...meterState,
      showMeterRegwall: !isSurfCheckEligible,
      isMeteredPremium: isSurfCheckEligible,
      showSurfCheckCounter: isSurfCheckEligible,
      modalToRender,
    };
  }
  return defaultMeterState;
};

export default useMetering;
